import Vue from 'vue';
import Vuex from 'vuex';
import pathify, { make } from 'vuex-pathify';
import registration from './modules/registration';
import behaviorTracking from './modules/behaviorTracking';
import { enrichSorcererPayments } from '../lib/enrichSorcererPayments';
import railsAxios from '../lib/railsAxios';
import captureSentryException from '../tracking/sentry/captureSentryException';
import updateDataAnalyticsLayer from '../tracking/adobe/updateDataAnalyticsLayer';

Vue.use(Vuex);

const state = {
  isLoggedIn:           false,
  awaitingNavigation:   false,
  backendErrors:        null,
  backendErrorMessages: null,
  account:              {
    preapproval_campaign_id: null,
    account_id:              null,
  },
  loanApplication:  null,
  loanApplications: null,
  latestLoan:       null,
  sessionQuote:     null,
  rails_session_id: null,
};

export const getters = {
  ...make.getters(state),
  canEnterLoanOffers(currentState) {
    let allowedToEnter;
    const { loanApplication, sessionQuote } = currentState;
    try {
      if (typeof loanApplication === 'undefined' || loanApplication === null) {
        return false;
      }
      const loanOffers = loanApplication.quotes;
      const loanApplicationStatus = loanApplication.loan_application_status;
      const allowedStates = ['underwriting_approved', 'constract_unsigned'];
      const validStatus = allowedStates.includes(loanApplicationStatus);
      const hasOffers = typeof loanOffers !== 'undefined' && loanOffers !== null;
      allowedToEnter = validStatus && hasOffers;
    } catch (e) {
      const extras = {
        sessionQuote,
        loanApplication,
      };
      if (loanApplication !== null && typeof loanApplication !== 'undefined') {
        extras.loanOffers = loanApplication.quotes;
      }
      captureSentryException(e, extras);
      allowedToEnter = false;
    }

    return allowedToEnter;
  },
  hasLoanApplications(currentState) {
    const loanApplicationsState = currentState.loanApplications;
    return loanApplicationsState !== null;
  },
  hasLoanApplication(currentState) {
    const loanApplicationsState = currentState.loanApplication;
    return loanApplicationsState !== null;
  },
  payments(currentState) {
    const { sorcererPayments } = currentState.latestLoan;
    const { unpaidInstallments, paymentPlans } = currentState.sysopen;
    const paid = sorcererPayments.paid_installments;
    const unpaid = sorcererPayments.unpaid_installments;

    return enrichSorcererPayments(paid, unpaid, unpaidInstallments, paymentPlans);
  },
  hasInstallmentsInDefault(currentState, storeGetters) {
    let defaultedPayments = [];
    defaultedPayments = Vue.lodash.filter(storeGetters.payments, (p) => p.status === 'defaulted');

    return defaultedPayments.length > 0;
  },
  allDefaultedPayments(currentState, storeGetters) {
    let defaultedPayments = [];
    defaultedPayments = Vue.lodash.filter(storeGetters.payments, (p) => p.status === 'defaulted');

    return defaultedPayments;
  },
  maxDaysInDefault(currentState, storeGetters) {
    const today = new Date();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.max(...storeGetters.allDefaultedPayments.map((payment) => {
      const diffTime = today.getTime() - Date.parse(payment.installment_due_date);
      return Math.round(diffTime / oneDay);
    }));
  },
  allPaymentPlansOnTime(currentState) {
    const { paymentPlans } = currentState.sysopen;
    if (paymentPlans.length === 0) {
      return true;
    }

    const latePaymentPlans = Vue.lodash.filter(paymentPlans, (plan) => {
      const lateInstallment = plan.installments.find((installment) => installment.status === 'late');
      if (lateInstallment) {
        return true;
      }
      return false;
    });

    return latePaymentPlans.length === 0;
  },
};

export const mutations = {
  ...make.mutations(state),
  MERGE_ROOT_STATE(oldState, newState) {
    const newRootState = { ...newState };
    delete newRootState.registration;
    Vue.lodash.merge(oldState, newRootState);
    if (newState.loanApplication != null) {
      Vue.lodash.assign(oldState.loanApplication, newState.loanApplication);
    }
  },
};

export const actions = {
  ...make.actions(state),
  async mergeState({ commit, dispatch }, payload) {
    commit('MERGE_ROOT_STATE', payload);
    if (payload !== null && payload.registration !== null) {
      await dispatch('registration/mergeRegistrationState', payload.registration);
    }
  },
  async fetchAndUpdateState({ dispatch }) {
    return new Promise((resolve) => {
      try {
        railsAxios.get(`/v/fetch-state?${Date.now()}`).then((response) => {
          const { data } = response.data;

          if (data.current_path !== '' && !window.location.pathname.match(data.current_path)) {
            window.location.href = data.current_path;
            resolve(false);
          } else {
            dispatch('mergeState', data);
            updateDataAnalyticsLayer(data.data_layer);
            resolve(true);
          }
        });
      } catch (error) {
        // Logging happens in railsAxios
        resolve(false);
      }
    });
  },
  async fetchAndUpdateAccountHomeState({ dispatch }) {
    try {
      const response = await railsAxios.get(`/api/v1/account-home?${Date.now()}`);
      const { data } = response.data;
      dispatch('mergeState', data);
    } catch (error) {
      // Logging happens in railsAxios
    }
  },
  setBackendErrorMessage({ commit }, payload) {
    let errorMessage = 'Ocorreu um erro enquanto processávamos o seu pedido. Por favor, tente novamente.';
    if (payload) {
      errorMessage = payload;
    }
    commit('SET_BACKEND_ERROR_MESSAGES', errorMessage);
  },
  async canEnterLoanOffers(context) {
    let isComplete = context.getters.canEnterLoanOffers;
    if (!isComplete) {
      const stateUpdated = await context.dispatch('fetchAndUpdateState', undefined, { root: true });
      if (!stateUpdated) {
        return 'redirect';
      }
      isComplete = context.getters.canEnterLoanOffers;
    }
    return isComplete;
  },
  async hasAccountHome(context) {
    let isComplete = context.getters.hasLoanApplications;
    if (!isComplete) {
      await context.dispatch('fetchAndUpdateAccountHomeState', undefined, { root: true });
      isComplete = context.getters.hasLoanApplications;
    }
    return isComplete;
  },
  async fetchLoanStatusData({ dispatch }) {
    const payments = state.latestLoan.sorcererPayments;
    const operationNumber = payments.paid_installments.length > 0
      ? payments.paid_installments[0].operation_number
      : payments.unpaid_installments[0].operation_number;

    const response = await railsAxios.get(`/api/v1/payment-plans/loan-status?operation_number=${operationNumber}&${Date.now()}`);
    const { data } = response.data;
    await dispatch('mergeState', data);
  },
  async canEnterPreLoanOfferEBS(context) {
    if (!this.getters.hasLoanApplication) {
      const completed = await context.dispatch('fetchAndUpdateState', undefined, { root: true });
      if (!completed) {
        return 'redirect';
      }
    }

    if (!this.state.loanApplication || this.state.loanApplication.loan_application_status !== 'applied') {
      return false;
    }

    if (!this.state.registration.ebs) {
      return false;
    }

    return true;
  },
  async handleLoginCheck({ commit, dispatch }, payload) {
    commit('SET_IS_LOGGED_IN', !!payload.account_id);
    commit('SET_ACCOUNT', { account_id: payload.account_id });
    commit('SET_RAILS_SESSION_ID', payload.rails_session_id);
    dispatch('behaviorTracking/sendStoredEvents', payload);
  },
};

const store = new Vuex.Store({
  plugins: [pathify.plugin],
  state,
  getters,
  mutations,
  actions,
  modules: {
    registration,
    behaviorTracking,
  },
});

export default store;
